import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Loader2, Facebook, ShieldCheck } from 'lucide-react';
import api from '../lib/api';

const FB_SDK_ID = 'facebook-jssdk';

/**
 * WhatsApp Embedded Signup — Meta Tech Provider onboarding.
 * Launches the FB.login popup (config_id), captures the authorization `code` plus
 * the WABA / phone IDs from the cross-frame WA_EMBEDDED_SIGNUP message, optionally
 * collects a 6-digit 2FA PIN, then hands everything to the backend which performs
 * the server-to-server token exchange and registration.
 */
export const WhatsAppEmbeddedSignup = ({ onConnected }) => {
  const [config, setConfig] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [welcomeTo, setWelcomeTo] = useState('');

  const codeRef = useRef(null);
  const sessionRef = useRef(null); // { waba_id, phone_number_id }

  // 1) Load Embedded Signup config (non-secret app_id / config_id) from backend
  useEffect(() => {
    let active = true;
    api.get('/api/social-connectors/whatsapp/embedded-signup/config')
      .then((res) => { if (active) setConfig(res.data || null); })
      .catch(() => { if (active) setConfig({ enabled: false }); });
    return () => { active = false; };
  }, []);

  // 2) Inject the Facebook JS SDK once we have the app_id
  useEffect(() => {
    if (!config?.enabled || !config.app_id) return;

    const init = () => {
      if (!window.FB) return;
      window.FB.init({
        appId: config.app_id,
        cookie: true,
        xfbml: false,
        version: config.graph_version || 'v23.0',
      });
      setSdkReady(true);
    };

    if (window.FB) { init(); return; }
    window.fbAsyncInit = init;
    if (!document.getElementById(FB_SDK_ID)) {
      const js = document.createElement('script');
      js.id = FB_SDK_ID;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      js.async = true; js.defer = true; js.crossOrigin = 'anonymous';
      document.body.appendChild(js);
    }
  }, [config]);

  // 3) Listen for the WA_EMBEDDED_SIGNUP session-info message from the Meta popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (typeof event.origin !== 'string' || !event.origin.endsWith('facebook.com')) return;
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
      if (data.event === 'FINISH' && data.data) {
        sessionRef.current = {
          waba_id: data.data.waba_id,
          phone_number_id: data.data.phone_number_id,
        };
        maybeProceed();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Once BOTH the auth code and the session info have arrived, ask for the PIN
  const maybeProceed = useCallback(() => {
    if (codeRef.current && sessionRef.current) {
      setLaunching(false);
      setPinOpen(true);
    }
  }, []);

  const fbLoginCallback = useCallback((response) => {
    if (response?.authResponse?.code) {
      codeRef.current = response.authResponse.code;
      maybeProceed();
    } else {
      setLaunching(false);
      toast.info('WhatsApp signup was cancelled');
    }
  }, [maybeProceed]);

  const launch = () => {
    if (!window.FB || !config?.config_id) { toast.error('Facebook SDK not ready yet'); return; }
    codeRef.current = null;
    sessionRef.current = null;
    setLaunching(true);
    window.FB.login(fbLoginCallback, {
      config_id: config.config_id,
      response_type: 'code',
      override_default_response_type: true,
      extras: { sessionInfoVersion: '3', version: 'v4' },
    });
  };

  const submit = async () => {
    if (!codeRef.current || !sessionRef.current) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/social-connectors/whatsapp/embedded-signup', {
        code: codeRef.current,
        waba_id: sessionRef.current.waba_id,
        phone_number_id: sessionRef.current.phone_number_id,
        pin: pin.trim() || null,
        welcome_to: welcomeTo.trim() || null,
      });
      const num = res.data?.display_phone_number || res.data?.phone_number_id;
      toast.success('WhatsApp Business connected', {
        description: res.data?.welcome_sent
          ? 'A welcome message is on its way to your number 🎉'
          : (num ? `Number: ${num}` : undefined),
      });
      setPinOpen(false);
      setPin('');
      setWelcomeTo('');
      codeRef.current = null;
      sessionRef.current = null;
      onConnected && onConnected();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error('Connection failed', { description: typeof detail === 'string' ? detail : 'Could not complete Embedded Signup' });
    } finally {
      setSubmitting(false);
    }
  };

  if (config && !config.enabled) return null; // server not configured → hide, manual paste remains

  return (
    <div className="space-y-2" data-testid="whatsapp-embedded-signup">
      <Button
        type="button"
        onClick={launch}
        disabled={!sdkReady || launching}
        className="w-full bg-[#1877f2] hover:bg-[#166fe0] text-white"
        data-testid="whatsapp-es-launch-btn"
      >
        {launching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Facebook className="h-4 w-4 mr-2" />}
        {sdkReady ? 'Connect with Facebook' : 'Loading…'}
      </Button>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-green-500 shrink-0" />
        <span>Recommended — create or select your WhatsApp Business Account in one guided flow. No tokens to copy.</span>
      </div>

      <Dialog open={pinOpen} onOpenChange={(o) => { if (!submitting) setPinOpen(o); }}>
        <DialogContent className="max-w-sm" data-testid="whatsapp-es-pin-dialog">
          <DialogHeader>
            <DialogTitle>Almost done</DialogTitle>
            <DialogDescription>
              Enter the 6-digit two-factor PIN for this WhatsApp number to finish registration. Leave blank if it is already registered.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit PIN (optional)"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            data-testid="whatsapp-es-pin-input"
          />
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Send a welcome message to (optional)</label>
            <Input
              type="text"
              inputMode="tel"
              placeholder="Your WhatsApp number, e.g. 14155551234"
              value={welcomeTo}
              onChange={(e) => setWelcomeTo(e.target.value.replace(/[^\d+]/g, ''))}
              data-testid="whatsapp-es-welcome-input"
            />
            <p className="text-[11px] text-muted-foreground">We'll send a quick "you're connected" message to confirm everything works.</p>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={submit} disabled={submitting} data-testid="whatsapp-es-finish-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Finish & Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppEmbeddedSignup;
